import {type Ref, useRef, useState} from 'react'
import {AnchoredOverlay, Button, IconButton} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {StackIcon, TriangleDownIcon, XIcon} from '@primer/octicons-react'
import {useMediaQuery} from '@github-ui/use-media-query'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {getBaseBranchText} from '../utils/branch-label'
import {FeatureState, type StackData} from '../page-data/payloads/header'
import {useUnstackPullRequestsMutation} from '../mutations/use-unstack-pull-requests-mutation'
import {useQueryClient} from '@github-ui/react-query'
import {useHeaderPageDataQueryKey} from '../page-data/loaders/use-header-page-data'
import {useLocation} from '@github-ui/react-router'
import {getPullRequestTabSuffix} from '@github-ui/paths'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {UnstackDialog} from './UnstackButtonDialog'
import {StackOverlay} from './StackOverlay'
import styles from './StackState.module.css'
import {useFeatureState} from '../hooks/use-feature-state'
import {buildPullRequestUserActionContext} from '../analytics/pull-request-user-action'
import {usePreviewStackQueryKey} from '../page-data/loaders/use-preview-stack-data'
import {StackActionsMenu} from './StackActionsMenu'

type StackStateProps = {
  anchorVariant?: 'default' | 'compact'
  overlayPortalContainerName?: string
  pullRequestNumber: number
  stack: StackData | undefined | null
  viewerCanUnstack: boolean
}

type StackStateAnchorButtonProps = {
  anchorRef: Ref<HTMLButtonElement>
  ariaHasPopup: 'dialog' | 'true'
  onClick: () => void
  open: boolean
  position: number
  size: number
}

// Temporary easter egg for launch gated behind the `pull_request_stacks_pancake_icon` FF
function PancakeLeadingVisual() {
  return (
    <span className={styles.pancakeLeadingVisual} aria-hidden="true">
      🥞
    </span>
  )
}

function CompactStackLeadingVisual() {
  return <StackIcon size={12} />
}

function StackStateAnchorButton({anchorRef, ariaHasPopup, onClick, open, position, size}: StackStateAnchorButtonProps) {
  const isPancakeIconEnabled = isFeatureEnabled('pull_request_stacks_pancake_icon')

  return (
    <Button
      ref={anchorRef}
      className={styles.stackBadge}
      size="medium"
      variant="invisible"
      leadingVisual={isPancakeIconEnabled ? PancakeLeadingVisual : StackIcon}
      aria-label={`Navigate pull request stack, position ${position} of ${size}`}
      aria-expanded={open}
      aria-haspopup={ariaHasPopup}
      onClick={onClick}
    >
      {position}/{size}
    </Button>
  )
}

function CompactStackStateAnchorButton({
  anchorRef,
  ariaHasPopup,
  onClick,
  open,
  position,
  size,
}: StackStateAnchorButtonProps) {
  return (
    <Button
      ref={anchorRef}
      className="fgColor-muted"
      size="small"
      variant="invisible"
      leadingVisual={CompactStackLeadingVisual}
      trailingAction={TriangleDownIcon}
      aria-label={`Navigate pull request stack, position ${position} of ${size}`}
      aria-expanded={open}
      aria-haspopup={ariaHasPopup}
      onClick={onClick}
    >
      {position}/{size}
    </Button>
  )
}

export function StackState({
  anchorVariant = 'default',
  overlayPortalContainerName,
  pullRequestNumber,
  stack,
  viewerCanUnstack,
}: StackStateProps) {
  const isPullRequestStacksEnabled = useFeatureState('pullRequestStacks') === FeatureState.Enabled
  const [open, setOpen] = useState<boolean>(false)
  const [isConfirmingUnstack, setIsConfirmingUnstack] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState('')
  const stackActionsMenuAnchorRef = useRef<HTMLButtonElement>(null)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const currentEntryRef = useRef<HTMLAnchorElement>(null)

  const queryClient = useQueryClient()
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const headerPageDataQueryKey = useHeaderPageDataQueryKey()
  const previewStackQueryKey = usePreviewStackQueryKey()
  const {mutate: mutateUnstackPullRequests, isPending: isLoading} = useUnstackPullRequestsMutation()
  const {pathname} = useLocation()
  // Primer's narrow viewport range
  const isNarrow = useMediaQuery('(max-width: calc(48rem - 0.02px))')

  if (!isPullRequestStacksEnabled) {
    return null
  }

  if (!stack) {
    return null
  }

  const {baseBranch, repositoryOwnerLogin, repositoryName, position, size, pulls, allPullsLocked} = stack
  const stackAnalytics = {
    id: stack.id,
    number: stack.number,
    created_at: stack.createdAt,
  }
  const baseBranchText = getBaseBranchText(baseBranch, repositoryOwnerLogin, repositoryOwnerLogin)

  // Pulls array is ordered from head to base, so top pull in the stack is the first item in the array.
  const topPullInStack = pulls[0]
  const addToStackBaseBranch = topPullInStack?.headBranch ?? baseBranch
  const addToStackHeadBranch = addToStackBaseBranch // Use same branch for now, until we have a "smarter" way of determining head ref for new PR
  const addToStackURL = `/${repositoryOwnerLogin}/${repositoryName}/compare/${addToStackBaseBranch}...${addToStackHeadBranch}?stack=true`

  // Preserve the current tab (e.g. /changes, /commits) when navigating between stacked PRs
  const tabSuffix = getPullRequestTabSuffix(pathname)

  const unstackInactiveText = !viewerCanUnstack
    ? "You don't have permission"
    : allPullsLocked
      ? 'This stack cannot be unstacked'
      : undefined

  const someLockedPulls = pulls.some(pull => !pull.isUnstackable)

  const onClickUnstack = () => {
    sendClickAnalyticsEvent(
      buildPullRequestUserActionContext({
        category: 'stack_navigator',
        action: 'unstack_click',
        data: {
          stack: stackAnalytics,
          stack_size: size,
        },
      }),
    )
    setIsConfirmingUnstack(true)
  }

  // Header shared by wide-viewport AnchoredOverlay and narrow-viewport Dialog
  const renderStackOverlayHeader = ({titleId, onClose}: {titleId?: string; onClose: () => void}) => {
    const isDialog = Boolean(titleId)

    return (
      <div className={styles.overlayHeader}>
        <div className={styles.overlayHeaderContent}>
          {isDialog ? (
            <Dialog.Title id={titleId} className={styles.overlayHeaderTitle}>
              Stack #{stack.number}
            </Dialog.Title>
          ) : (
            <span className={styles.overlayHeaderTitle}>Stack #{stack.number}</span>
          )}
        </div>
        <div className={styles.overlayHeaderActions}>
          <StackActionsMenu
            anchorRef={stackActionsMenuAnchorRef}
            onUnstack={onClickUnstack}
            unstackInactiveText={unstackInactiveText}
          />
          <IconButton icon={XIcon} variant="invisible" aria-label="Close" onClick={onClose} />
        </div>
      </div>
    )
  }

  const stackNavContent = (
    <StackOverlay
      pullRequestNumber={pullRequestNumber}
      stack={stack}
      baseBranchText={baseBranchText}
      addToStackURL={addToStackURL}
      showShortcutGuidance={isFeatureEnabled('pull_request_stacks_navigation_shortcuts')}
      tabSuffix={tabSuffix}
      currentEntryRef={currentEntryRef}
      onSwitchBranch={(targetPullRequestId, targetPositionInStack) => {
        sendClickAnalyticsEvent(
          buildPullRequestUserActionContext({
            category: 'stack_navigator',
            action: 'switch_branch',
            data: {
              stack: stackAnalytics,
              target_pull_request_id: targetPullRequestId,
              target_position_in_stack: targetPositionInStack,
            },
          }),
        )
      }}
      onAddToStackClick={() => {
        sendClickAnalyticsEvent(
          buildPullRequestUserActionContext({
            category: 'stack_navigator',
            action: 'add_to_stack_click',
            data: {
              stack: stackAnalytics,
              stack_size_before: size,
            },
          }),
        )
      }}
    />
  )

  const handleAnchorClick = () => {
    const isOpening = !open
    setOpen(isOpening)

    if (isOpening) {
      sendClickAnalyticsEvent(
        buildPullRequestUserActionContext({
          category: 'stack_navigator',
          action: 'open',
          data: {
            stack: stackAnalytics,
            source: 'pr_header',
          },
        }),
      )
      // Focus the active entry once the overlay has opened and its focus trap has run, so a
      // large stack scrolls to it. Handled on the open event rather than via an effect on `open`.
      requestAnimationFrame(() => currentEntryRef.current?.focus())
    }
  }

  const anchorButtonProps = {
    anchorRef,
    ariaHasPopup: isNarrow ? ('dialog' as const) : ('true' as const),
    onClick: handleAnchorClick,
    open,
    position,
    size,
  }

  return (
    <div className="d-flex flex-items-center fgColor-muted">
      {anchorVariant === 'compact' ? (
        <CompactStackStateAnchorButton {...anchorButtonProps} />
      ) : (
        <StackStateAnchorButton {...anchorButtonProps} />
      )}
      {isNarrow ? (
        <>
          {open && (
            <Dialog
              renderHeader={({dialogLabelId, onClose: closeDialog}) =>
                renderStackOverlayHeader({titleId: dialogLabelId, onClose: () => closeDialog('close-button')})
              }
              onClose={() => setOpen(false)}
              position={{narrow: 'bottom', regular: 'center'}}
              height="auto"
              initialFocusRef={currentEntryRef}
              renderBody={({children}) => <Dialog.Body className="p-0">{children}</Dialog.Body>}
            >
              {stackNavContent}
            </Dialog>
          )}
        </>
      ) : (
        <AnchoredOverlay
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          renderAnchor={null}
          anchorRef={anchorRef}
          overlayProps={overlayPortalContainerName ? {portalContainerName: overlayPortalContainerName} : undefined}
          width="large"
          focusTrapSettings={{initialFocusRef: currentEntryRef}}
        >
          <div>
            {renderStackOverlayHeader({onClose: () => setOpen(false)})}
            {stackNavContent}
          </div>
        </AnchoredOverlay>
      )}
      <UnstackDialog
        returnFocusRef={stackActionsMenuAnchorRef}
        isConfirmingUnstack={isConfirmingUnstack}
        isLoading={isLoading}
        errorMessage={errorMessage}
        someLockedPulls={someLockedPulls}
        onCancelUnstack={() => {
          setIsConfirmingUnstack(false)
          setErrorMessage('')
        }}
        onConfirmUnstack={() => {
          setErrorMessage('')
          mutateUnstackPullRequests(
            {stackId: stack.id},
            {
              onError: error => setErrorMessage(error.message),
              onSuccess: async () => {
                try {
                  queryClient.resetQueries({queryKey: previewStackQueryKey})
                  await queryClient.refetchQueries({queryKey: headerPageDataQueryKey}, {throwOnError: true})
                  setIsConfirmingUnstack(false)
                  setOpen(false)
                } catch {
                  setErrorMessage('Something unexpected happened. Please refresh the page.')
                }
              },
            },
          )
        }}
      />
    </div>
  )
}
